import { ColumnDef } from '@tanstack/react-table'
import { DownloadSearchResultButton } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsSearchActions/DownloadSearchResultButton'
import { parseRichTextSummary } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { MiddleTruncatedText } from 'features/knowledgeDocumentViewer/components/MiddleTruncatedText'
import {
  getCantileverDocumentId,
  getSystemDocumentBqId,
  type SystemDocumentEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { compareRevisionCodes } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import {
  formatDocumentDisplayTitle,
  toTitleCase,
} from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { InputType } from 'types'

export type DocumentTableRow = {
  id: string
  title: string
  filename: string
  documentTitle: string | null
  system: string | null
  vendor: string | null
  docType: string | null
  drawing: string | null
  timestamp: string | null
  numPages: number | null
  revisionCode: string | null
  revisionDate: string | null
  summary: string | null
  keywords: string[]
  /** Bare BigQuery document_id for UUID5 cantilever host derive. */
  bqDocumentId?: string
  /** Cantilever-document host id when tagged on the hydrated system document. */
  cantileverDocumentId?: string
}

export const DocumentTableColumns = (): ColumnDef<DocumentTableRow>[] => [
  {
    id: 'title',
    accessorKey: 'title',
    header: 'File Name',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 140,
    cell: ({ row }) => {
      const titleCasedTitle = toTitleCase(row.original.title)
      return (
        <MiddleTruncatedText
          text={titleCasedTitle}
          maxWidth='100%'
          fillWidth
          tooltipFontSize='0.75rem'
        />
      )
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'documentTitle',
    accessorKey: 'documentTitle',
    header: 'Document Title',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 140,
    cell: ({ row }) => {
      const value = row.original.documentTitle
        ? formatDocumentDisplayTitle(
            row.original.documentTitle,
            row.original.title
          )
        : '-'
      return (
        <MiddleTruncatedText
          text={value}
          maxWidth='100%'
          fillWidth
          tooltipFontSize='0.75rem'
        />
      )
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'numPages',
    accessorKey: 'numPages',
    header: 'Length',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      return <span>{row.original.numPages ?? 1}</span>
    },
  },
  {
    id: 'system',
    accessorKey: 'system',
    header: 'System',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 110,
    cell: ({ row }) => {
      return <span>{row.original.system || '-'}</span>
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'vendor',
    accessorKey: 'vendor',
    header: 'Vendor',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'multiSelectFilter',
    minSize: 110,
    cell: ({ row }) => {
      return <span>{row.original.vendor || '-'}</span>
    },
    meta: {
      inputType: InputType.MultiSelect,
    },
  },
  {
    id: 'docType',
    accessorKey: 'docType',
    header: 'Type',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'multiSelectFilter',
    minSize: 120,
    size: 150,
    cell: ({ row }) => {
      return <span>{row.original.docType || '-'}</span>
    },
    meta: {
      inputType: InputType.MultiSelect,
    },
  },
  {
    id: 'drawing',
    accessorKey: 'drawing',
    header: 'Drawing Number',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: true,
    filterFn: 'includesString',
    minSize: 120,
    cell: ({ row }) => {
      return <span>{row.original.drawing || '-'}</span>
    },
    meta: {
      inputType: InputType.Text,
    },
  },
  {
    id: 'revisionCode',
    accessorKey: 'revisionCode',
    header: 'Rev',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    sortingFn: (rowA, rowB) => {
      const revA = rowA.original.revisionCode ?? ''
      const revB = rowB.original.revisionCode ?? ''
      return compareRevisionCodes(revA, revB)
    },
    cell: ({ row }) => {
      return <span>{row.original.revisionCode || '-'}</span>
    },
  },
  {
    id: 'revisionDate',
    accessorKey: 'revisionDate',
    header: 'Date',
    enableSorting: true,
    sortDescFirst: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      return <span>{row.original.revisionDate || '-'}</span>
    },
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    enableColumnFilter: false,
    minSize: 48,
    size: 48,
    cell: ({ row }) => (
      <DownloadSearchResultButton document={row.original} size='icon' />
    ),
  },
]

export const transformDocumentsToTableData = (
  systemDocumentEntries: SystemDocumentEntry[]
): DocumentTableRow[] => {
  return systemDocumentEntries.map((entry) => {
    const { summary, keywords } = parseRichTextSummary(
      entry.entry.data.rich_text_summary
    )
    return {
      id: entry.entry.id,
      title: entry.entry.data.title,
      filename: entry.entry.data.filename || entry.entry.data.title,
      documentTitle: entry.entry.data.document_title || null,
      system: entry.entry.data.system || null,
      vendor: entry.entry.data.vendor_name || null,
      docType: entry.entry.data.document_type || null,
      drawing: entry.entry.data.drawing_number || null,
      timestamp: entry.entry.data.uploadDate || null,
      numPages: entry.entry.data.total_pages || null,
      revisionCode: entry.entry.data.revision_code || null,
      revisionDate: entry.entry.data.revision_date || null,
      summary,
      keywords,
      bqDocumentId: getSystemDocumentBqId(entry),
      cantileverDocumentId: getCantileverDocumentId(entry),
    }
  })
}
